import { Component } from "@angular/core";
import { CheckGroupPermissions } from "@helpers/checkGroupPermissions";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { Client } from "@interfaces/models/client.interface";
import { EdiOrderTableMenuRow } from "@interfaces/ediOrderTableMenuRow";
import { ImportEdiOrdersCSV } from "@interfaces/importEdiOrdersCSV.interface";
import { ReduceArrearsOnParts } from "@interfaces/reduceArrearsOnParts.interface";
import { ReduceArrearsOnPartsLineItem } from "@interfaces/reduceArrearsOnPartsLineItem.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { ClientService } from "@services/client.service";
import { EdiMessageService } from "@services/ediMessage.service";
import { EdiOrderService } from "@services/ediOrder.service";
import { FileService } from "@services/file.service";
import { PartService } from "@services/part.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "ediOrders",
	styleUrls: ["ediOrders.css"],
	templateUrl: "ediOrders.html"
})
export class EdiOrdersAdminComponent {
	clients: Client[] = [];
	deleteEnabled = true;
	ediOrderTableMenu: EdiOrderTableMenuRow[] = [];
	editEnabled = true;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	isSuperAdmin = false;
	itemType = "Part Order"
	itemUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	private logoErrorIds = new Set<number | string>();
	orderTable: any;
	popupAddButtonOptions: any = { onClick: () => this.importCSV(), text: "Import" };
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupImportDetails: ImportEdiOrdersCSV = new ImportEdiOrdersCSV();
	popupReduceArrearsOnParts: ReduceArrearsOnParts = new ReduceArrearsOnParts();
	popupTitle = "Import Edi Orders Message CSV";
	popupVisible = false;
	reduceArrearsOnPartsLineItems: ReduceArrearsOnPartsLineItem[] = [];
	selectedMenuItem: EdiOrderTableMenuRow = new EdiOrderTableMenuRow();
	showArrears = false;
	title: string;
	validationErrors: string[] = [];

	constructor(private appSettingsService: AppSettingsService, private toastService: ToastService, private clientService: ClientService, private itemService: EdiOrderService, private ediMessageService: EdiMessageService, private fileService: FileService, private partService: PartService) {
		this.title = `${this.itemType}s`;
		this.isSuperAdmin = CheckGroupPermissions(appSettingsService, "Super Admin");
		this.getClients();
		this.getMenu();
		this.setArrearsDisplay = this.setArrearsDisplay.bind(this);
		this.uploadCSVForImport = this.uploadCSVForImport.bind(this);

		const showArrearsSetting = this.appSettingsService.get("showArrearsOnEdiGrid");
		if (showArrearsSetting !== null && showArrearsSetting !== undefined && showArrearsSetting === "true") {
			this.showArrears = true;
		}
	}

	// Every part on the loaded order table carrying arrears, as the Reduce Arrears grid's starting point.
	// Originally only built for clients not sent ASNs via EDI (FLOW-502); FLOW-970 opened it up to all of
	// them, because an arrears figure nothing is going to deliver sits on the part either way.
	buildArrearsLineItems() {
		this.reduceArrearsOnPartsLineItems = [];

		this.orderTable.rows.forEach((row: any) => {
			if (row.deliveryArrears > 0) {
				this.reduceArrearsOnPartsLineItems.push({ currentArrears: row.deliveryArrears, partId: row.partId, partNumber: row.partNumber, reduceArrearsBy: row.deliveryArrears });
			}
		});
	}

	buildButtons() {
		this.headerPrimaryButtons = [];
		this.headerPrimaryButtons.push({ method: "downloadPickListPdf", text: "Pick List" });
		this.headerPrimaryButtons.push({ icon: "fal fa-upload", method: "importCSVPopup", text: "Import Edi Orders Message CSV" });

		if (this.selectedMenuItem != null && this.reduceArrearsOnPartsLineItems.length > 0) {
			this.headerPrimaryButtons.push({ method: "reduceArrearsPopup", text: "Reduce Arrears" });
		}
	}

	closePopup() {
		this.popupVisible = false;
	}

	downloadPickListPdf() {
		const date = new Date();
		this.itemService.pickList().subscribe(
			(response: any) =>{
				const dataType = response.type;
				const binaryData = [];
				binaryData.push(response);
				const downloadLink = document.createElement("a");
				downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
				downloadLink.setAttribute("download", date.toDateString() + "_EDI_Picklist.pdf");
				document.body.appendChild(downloadLink);
				downloadLink.click();
			}
		);
	}

	getClients() {
		this.clientService.getEdiClients()
			.subscribe(
				(res: any) => {
					this.clients = res.response;
					this.clients.sort((a,b) => a.name.localeCompare(b.name));
				},
				(err) => console.log(err)
			);
	}

	getLogoUrl(name: string, supplierId?: number | string): string | null {
		// If we’ve already seen an error for this supplier, force fallback
		if (supplierId != null && this.logoErrorIds.has(supplierId)) return null;

		if (!name) return null;
		if (name.toLowerCase().includes("jaguar")){
			name = "jlr";
		}
		// logo.dev expects a slug-ish value in the URL
		const slug = encodeURIComponent(
			name
				.trim()
				.toLowerCase()
				.replace(/&/g, "and")
				.replace(/[^a-z0-9\s-]/g, "")
				.replace(/\s+/g, "-")
		);

		return `https://img.logo.dev/name/${slug}?token=pk_VmwfGmJkTyKosIBaRGhZFw`;
	}

	getMenu() {
		this.itemService.getOrderTableClientAndLocationSelectionMenu()
			.subscribe(
				(res: any) => {
					this.ediOrderTableMenu = res.response;
					this.selectedMenuItem = this.ediOrderTableMenu[0];
					this.getOrderTable();
				},
				(err: any) => console.log(err)
			);
	}

	getOrderTable() {
		if (this.selectedMenuItem.isLocation === true) {
			this.itemService.getOrderTableByClientLocation(this.selectedMenuItem.id)
				.subscribe(
					(res: any) => {
						this.orderTable = res.response;
						this.buildArrearsLineItems();
						this.buildButtons();
					},
					(err) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					},
				);
		} else {
			this.itemService.getOrderTableByClient(this.selectedMenuItem.id)
				.subscribe(
					(res: any) => {
						this.orderTable = res.response;
						this.buildArrearsLineItems();
						this.buildButtons();
					},
					(err) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					},
				);
		}
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	importCSV() {
		this.validationErrors = [];
		this.ediMessageService.importOrdersCSV(this.popupImportDetails)
			.subscribe(
				() => {
					this.closePopup();
					this.toastService.success("CSV Imported");
					this.getOrderTable();
				},
				(err: any) => {
					this.validationErrors = err.file;
				}
			);
	}

	importCSVPopup() {
		this.validationErrors = [];
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.importCSV(), text: "Import" };
		this.popupImportDetails = new ImportEdiOrdersCSV();
		this.popupTitle = "Import Edi Orders Message CSV";
	}

	onSupplierLogoError(supplier: any): void {
		// Mark supplier as errored so we fallback next render
		if (supplier?.id != null) {
			this.logoErrorIds.add(supplier.id);
		}
	}

	reduceArrears() {
		this.popupReduceArrearsOnParts.parts = JSON.stringify(this.reduceArrearsOnPartsLineItems);
		this.partService.bulkReduceArrears(this.popupReduceArrearsOnParts)
			.subscribe(
				() => {
					this.closePopup();
					this.toastService.success("Arrears Reduced, reloading details");
					this.getOrderTable();
				},
				(err: any) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	reduceArrearsPopup() {
		this.popupVisible = true;
		this.popupAddButtonOptions = { onClick: () => this.reduceArrears(), text: "Reduce Arrears" };
		this.popupTitle = "Reduce Arrears";
	}

	setArrearsDisplay(e: any) {
		this.showArrears = e.value;
		this.appSettingsService.set("showArrearsOnEdiGrid", this.showArrears);
	}

	syncTreeViewSelection(data: any) {
		this.selectedMenuItem = data.itemData;
		this.getOrderTable();
	}

	uploadCSVForImport(file: File) {
		this.validationErrors = [];
		const containerName = "class-schedules";
		const uploadData = new FormData();
		uploadData.append("containerName", containerName);
		uploadData.append("files", file);
		this.fileService.uploadFile(uploadData)
			.subscribe(
				(res: any) => {
					this.popupImportDetails.fileLookupId = res.response[0].lookupId;
				},
				(err) => {
					console.log(err);
					this.toastService.error("CSV Upload Failed - Please ensure you are uploading a valid file");
				}
			);
	}
}